import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar, SidebarMenuButton } from "@/pages/AppSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Shield, Search, Download, Trash2, ArrowLeft, User, Eye,
  FileText, Clock, ChevronDown, ChevronUp, AlertTriangle, History
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const headers = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

export default function PatientDataPage() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [patients, setPatients] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [loginHistory, setLoginHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("data");
  const [expandedModule, setExpandedModule] = useState(null);

  const searchPatients = async () => {
    if (query.length < 2) { toast.error("Sisesta vähemalt 2 tähemärki"); return; }
    setSearching(true);
    setSelectedPatient(null);
    setPatientData(null);
    try {
      const res = await fetch(`${API}/admin/patients/search?q=${encodeURIComponent(query)}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setPatients(data);
        if (data.length === 0) toast.info("Patsiente ei leitud");
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Otsing ebaõnnestus");
      }
    } catch { toast.error("Võrguühenduse viga"); }
    setSearching(false);
  };

  const loadPatientData = async (pid) => {
    setSelectedPatient(pid);
    setLoading(true);
    setActiveTab("data");
    try {
      const res = await fetch(`${API}/admin/patients/${encodeURIComponent(pid)}/full-data`, { headers: headers() });
      if (res.ok) setPatientData(await res.json());
      else toast.error("Andmete laadimine ebaõnnestus");
    } catch { toast.error("Võrguühenduse viga"); }
    setLoading(false);
  };

  const loadAuditLog = async (pid) => {
    setActiveTab("audit");
    try {
      const res = await fetch(`${API}/admin/patients/${encodeURIComponent(pid)}/audit-log`, { headers: headers() });
      if (res.ok) setAuditLog(await res.json());
    } catch { /* ok */ }
  };

  const loadLoginHistory = async () => {
    setActiveTab("logins");
    if (loginHistory) return;
    try {
      const res = await fetch(`${API}/admin/login-history`, { headers: headers() });
      if (res.ok) setLoginHistory(await res.json());
    } catch { /* ok */ }
  };

  const exportData = () => {
    if (!patientData) return;
    const blob = new Blob([JSON.stringify(patientData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `patsient_${selectedPatient}_andmed.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Andmed eksporditud JSON failina");
  };

  const deleteAllData = async () => {
    if (!selectedPatient) return;
    const confirmed = window.prompt(`Sisesta patsiendi isikukood "${selectedPatient}" kinnitamiseks:`);
    if (confirmed !== selectedPatient) { toast.error("Isikukood ei klapi — kustutamine katkestatud"); return; }
    try {
      const res = await fetch(`${API}/admin/patients/${encodeURIComponent(selectedPatient)}/all-data`, { method: "DELETE", headers: headers() });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Kõik andmed kustutatud: ${JSON.stringify(result.details)}`);
        setPatientData(null);
        setSelectedPatient(null);
        setPatients(null);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Kustutamine ebaõnnestus");
      }
    } catch { toast.error("Võrguühenduse viga"); }
  };

  const renderModuleData = (title, items, moduleKey) => {
    if (!items?.length) return null;
    const isOpen = expandedModule === moduleKey;
    return (
      <div className="border border-slate-200 rounded-lg overflow-hidden mb-3" key={moduleKey}>
        <button className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 text-left" onClick={() => setExpandedModule(isOpen ? null : moduleKey)}>
          <span className="text-sm font-semibold text-slate-700">{title} ({items.length})</span>
          {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {isOpen && (
          <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
            {items.map((item, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-lg p-3 text-xs text-slate-600">
                <pre className="whitespace-pre-wrap break-words overflow-x-auto">{JSON.stringify(item, null, 2)}</pre>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64">
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
          <Shield className="w-5 h-5 text-slate-700" />
          <h1 className="text-lg font-bold text-slate-900" data-testid="patient-data-title">Patsiendi andmehaldus</h1>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4 mr-1" /> Keskuste haldus
          </Button>
        </header>

        <div className="max-w-4xl mx-auto p-4">
          {/* GDPR info banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 text-sm text-blue-800">
            <strong>GDPR vastavus:</strong> Siit saate otsida patsiendi andmeid, vaadata logisid, eksportida andmeid (Art. 20) ja kustutada kõik andmed (Art. 17 — õigus olla unustatud). Andmeid säilitatakse 10 aastat vastavalt Eesti tervishoiuteenuste seadusele (§ 769).
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5" data-testid="patient-search-section">
            <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> Otsi patsienti</h2>
            <div className="flex gap-2">
              <Input
                placeholder="Nimi või isikukood..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === "Enter" && searchPatients()}
                className="flex-1"
                data-testid="patient-search-input"
              />
              <Button onClick={searchPatients} disabled={searching} data-testid="patient-search-btn">
                {searching ? "Otsib..." : "Otsi"}
              </Button>
            </div>

            {/* Search results */}
            {patients && (
              <div className="mt-3 space-y-2">
                {patients.map((p, i) => (
                  <button
                    key={i}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${selectedPatient === p.patient_id ? "border-blue-400 bg-blue-50" : "border-slate-100 hover:bg-slate-50"}`}
                    onClick={() => loadPatientData(p.patient_id)}
                    data-testid={`patient-result-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center"><User className="w-4 h-4 text-slate-500" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-400">{p.patient_id}</div>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {p.modules?.map((m, j) => (
                          <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{m.type}</span>
                        ))}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Patient detail */}
          {selectedPatient && (
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="patient-detail-section">
              {/* Tabs */}
              <div className="flex gap-1 mb-4 border-b border-slate-100 pb-3">
                <Button size="sm" variant={activeTab === "data" ? "default" : "ghost"} onClick={() => setActiveTab("data")} data-testid="tab-data">
                  <FileText className="w-4 h-4 mr-1" /> Andmed
                </Button>
                <Button size="sm" variant={activeTab === "audit" ? "default" : "ghost"} onClick={() => loadAuditLog(selectedPatient)} data-testid="tab-audit">
                  <Eye className="w-4 h-4 mr-1" /> Vaatamiste logi
                </Button>
                <Button size="sm" variant={activeTab === "logins" ? "default" : "ghost"} onClick={loadLoginHistory} data-testid="tab-logins">
                  <History className="w-4 h-4 mr-1" /> Sisselogimised
                </Button>
              </div>

              {/* Data tab */}
              {activeTab === "data" && (
                <>
                  {loading ? (
                    <div className="text-center py-8 text-slate-400">Laadin andmeid...</div>
                  ) : patientData ? (
                    <>
                      {renderModuleData("Silmapõhja analüüsid", patientData.retinal, "retinal")}
                      {renderModuleData("Diabeetilised jalahaavandid", patientData.ulcers, "ulcers")}
                      {renderModuleData("Gravesi orbitopaatia", patientData.graves, "graves")}
                      {renderModuleData("Nõusolekud", patientData.consents, "consents")}
                      {!patientData.retinal?.length && !patientData.ulcers?.length && !patientData.graves?.length && !patientData.consents?.length && (
                        <div className="text-center py-6 text-slate-400">Andmeid ei leitud</div>
                      )}

                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                        <Button size="sm" variant="outline" onClick={exportData} data-testid="export-data-btn">
                          <Download className="w-4 h-4 mr-1" /> Ekspordi andmed (JSON)
                        </Button>
                        <Button size="sm" variant="destructive" onClick={deleteAllData} data-testid="delete-all-data-btn">
                          <Trash2 className="w-4 h-4 mr-1" /> Kustuta kõik andmed
                        </Button>
                      </div>
                      <div className="mt-2 flex items-start gap-2 text-xs text-red-500">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>Andmete kustutamine on pöördumatu! Kustutamiseks peate sisestama patsiendi isikukoodi kinnituseks. Kustutab andmed kõikidest moodulitest ja S3 salvestusest.</span>
                      </div>
                    </>
                  ) : null}
                </>
              )}

              {/* Audit log tab */}
              {activeTab === "audit" && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {auditLog?.length ? auditLog.map((log, i) => (
                    <div key={i} className="flex items-start gap-3 px-3 py-2 bg-slate-50 rounded-lg text-xs">
                      <Clock className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-700">{log.user_email}</span>
                        <span className="text-slate-400 mx-1">—</span>
                        <span className="text-slate-600">{log.action}</span>
                        <span className="text-slate-400 mx-1">·</span>
                        <span className="text-slate-400">{log.resource_type}</span>
                        {log.details && <span className="text-slate-400 ml-1">({log.details})</span>}
                      </div>
                      <span className="text-slate-400 shrink-0">{log.timestamp?.replace("T", " ").slice(0, 19)}</span>
                    </div>
                  )) : (
                    <div className="text-center py-6 text-slate-400">Logisid pole veel</div>
                  )}
                </div>
              )}

              {/* Login history tab */}
              {activeTab === "logins" && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {loginHistory?.length ? loginHistory.map((log, i) => (
                    <div key={i} className="flex items-start gap-3 px-3 py-2 bg-slate-50 rounded-lg text-xs">
                      <History className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-700">{log.email}</span>
                      </div>
                      <span className="text-slate-400 shrink-0">{log.created_at?.replace("T", " ").slice(0, 19)}</span>
                    </div>
                  )) : (
                    <div className="text-center py-6 text-slate-400">Sisselogimiste ajalugu puudub</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
